import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Common layout wrapper for auth pages
 * Provides consistent structure for Login, ForgotPassword, ResetPassword, SetupPassword
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Page subtitle
 * @param {React.ReactNode} props.icon - Custom icon element
 * @param {boolean} props.showFooter - Whether to show privacy/terms footer (default: true)
 * @param {boolean} props.showBackToLogin - Whether to show back to login link (default: false)
 */
const AuthPageLayout = ({
  children,
  title,
  subtitle,
  icon,
  showFooter = true,
  showBackToLogin = false
}) => {
  // Default icon if none provided
  const defaultIcon = (
    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4 login-container">
      <div className="w-full max-w-md">
        {/* Header */}
        {(title || icon) && (
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg mb-6">
              {icon || defaultIcon}
            </div>
            {title && (
              <h1 className="text-3xl font-bold mb-2 login-title">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="login-subtitle">
                {subtitle}
              </p>
            )}
          </div>
        )}

        {/* Content */}
        {children}

        {/* Back to Login Link */}
        {showBackToLogin && (
          <div className="text-center mt-4">
            <Link 
              to="/login" 
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Back to Login
            </Link>
          </div>
        )}

        {/* Footer */}
        {showFooter && (
          <div className="text-center mt-8">
            <p className="text-xs login-footer-text">
              © 2025 FitFlow.{' '}
              <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">
                Privacy Policy
              </Link>
              {' | '}
              <Link to="/terms" className="text-blue-600 hover:text-blue-800 underline">
                Terms
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Card wrapper for auth form content
 */
export const AuthCard = ({ children, className = '' }) => (
  <div className={`rounded-2xl shadow-xl p-8 login-card ${className}`}>
    {children}
  </div>
);

/**
 * Help text with admin contact link
 */
export const AuthHelpText = ({ children }) => (
  <div className="mt-6 text-center">
    <p className="text-sm login-footer-text">
      {children || (
        <>
          Need help? Contact your{' '}
          <a 
            href="https://younitefitness.co.nz" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            admin
          </a>
          .
        </>
      )}
    </p>
  </div>
);

export default AuthPageLayout;
